import React, { useState } from 'react';
import { X } from 'lucide-react';
import { OrderFormContent } from '@/pages/portal/trade/components/OrderFormContent';
import { TradingAccount } from '@/api/trading-accounts/use-trading-accounts';
import { AnimatePresence, motion } from 'framer-motion';

interface MobileOrderFormProps {
    isOpen: boolean;
    onClose: () => void;
    selectedPair: string;
    selectedInstrumentId?: string;
    realTimePrice?: { bid: number; ask: number; symbol?: string };
    selectedAccount?: TradingAccount | null;
    tradingAccounts?: TradingAccount[];
    onOrderSuccess?: () => void;
    isPricesLoading?: boolean;
    pricesError?: string | null;
    instrumentType?: 'forex' | 'crypto' | 'stocks' | 'commodities';
    instrumentData?: {
        min_lot: number;
        max_lot: number;
        max_leverage: number;
        leverage: number;
    };
    direction: 'buy' | 'sell';
}

const MobileOrderForm: React.FC<MobileOrderFormProps> = ({
    isOpen,
    onClose,
    selectedPair,
    selectedInstrumentId,
    realTimePrice,
    selectedAccount,
    tradingAccounts,
    onOrderSuccess,
    isPricesLoading = false,
    pricesError = null,
    instrumentType = 'forex',
    instrumentData,
    direction,
}) => {
    const handleOrderSuccess = () => {
        if (onOrderSuccess) {
            onOrderSuccess();
        }
        onClose();
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.5 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50"
                        onClick={onClose}
                    />

                    {/* Order Form Sheet */}
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed inset-x-0 bottom-0 top-20 z-50 trader-glass-strong rounded-t-[20px] overflow-hidden flex flex-col shadow-xl"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
                            <h2 className="text-lg font-semibold text-foreground">
                                {direction === 'buy' ? 'Buy' : 'Sell'} {selectedPair}
                            </h2>
                            <button
                                onClick={onClose}
                                className="p-2 hover:bg-foreground/10 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5 text-muted-foreground" />
                            </button>
                        </div>

                        {/* Form Content */}
                        <div className="flex-1 overflow-y-auto">
                            <div className="p-4">
                                <OrderFormContent
                                    onClose={onClose}
                                    selectedPair={selectedPair}
                                    selectedInstrumentId={selectedInstrumentId}
                                    realTimePrice={realTimePrice}
                                    selectedAccount={selectedAccount}
                                    tradingAccounts={tradingAccounts}
                                    onOrderSuccess={handleOrderSuccess}
                                    isPricesLoading={isPricesLoading}
                                    pricesError={pricesError}
                                    instrumentType={instrumentType}
                                    instrumentData={instrumentData}
                                    direction={direction}
                                />
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default MobileOrderForm;
